import { Avatar } from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { routingConstants } from "@/Constants/routingConstants";
import {
  fetchSubmittedTimesheets,
  setSubmittedTimesheetsLoading,
  updateSorting,
  updateSelectedCandidate,
  updateSelectedSignOffIds,
  updateToggleSelected,
  updatePagination,
  deSelectAllCandidate,
} from "@/reducers/timesheets/timesheetsubmitted";
import {
  expandButtonColumn,
  getColumnNormalHtml,
} from "@/utilities/datatable/columnFunctions";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";
import { getComplianceClass } from "@/utilities/utils";

import DataTablePaginationLoader from "../../../Skeletons/DataTablePaginationLoader";
import TableLoader from "../../TableLoader";
import TableNoContent from "../../TableNoContent";
import CandidateStatusAndInfo from "../dataTables/CandidateStatusAndInfo";
import BlurredText from "../managedService/BlurredText";
import { FilterModal } from "./Listing/Submitted/FilterModal";
import { Miles } from "../candidates/Listing/common/Miles";
import ClientPayPopover from "../dataTables/ClientPayPopover";
import SearchFilter from "./Listing/Submitted/SearchFilter";

const SubmittedList = () => {
  const dispatch = useDispatch();
  const {
    selectedCandidate,
    toggleSelected,
    isLoading,
    pagination,
    submittedTimesheets,
    totalCount,
    isSelectedAll,
    isPaginationLoading,
    sorting,
  } = useSelector((state) => state.clientTimesheetSubmitted);

  const { clientUserAllPermissions } = useSelector((state) => state.session);

  const [data, setData] = useState([]);
  const [diffList, setDiffList] = useState([]); // list of candidate which required edit on approve click
  useEffect(() => {
    if (selectedCandidate.length == 0) {
      dispatch(updateToggleSelected());
    }
  }, [selectedCandidate]);
  useEffect(() => {
    setData(submittedTimesheets);
  }, [submittedTimesheets]);
  const columns = [
    {
      name: "Candidate",
      sortable: true,
      sortField: "candidate_forename",
      selector: (row) => (
        <div className="flex gap-[14px]">
          <div className="min-w-[45px]">
            <Avatar
              alt={row.candidate_name}
              sx={{ width: 45, height: 45 }}
              src={row.profile_image ? row.profile_image : ""}
            />
          </div>
          <div className="flex grow flex-col gap-1 overflow-hidden">
            {!clientUserAllPermissions ||
            clientUserAllPermissions.is_allow_to_view_candidate_profile ===
              1 ? (
              <>
                <Link
                  to={routingConstants.candidateDetail(row.route_id)}
                  className="truncate text-[14px] font-semibold leading-[normal] text-black"
                >
                  {row.candidate_name}
                </Link>
              </>
            ) : (
              <span className="cursor-default truncate text-[14px] font-semibold leading-[normal] text-black">
                {row.candidate_name}
              </span>
            )}
            <CandidateStatusAndInfo
              sortlisted={row.candidate_sortlisted}
              unsuitable={row.candidate_unsuitable}
              is_auto_booked_on_apply={row.is_auto_booked_on_apply}
              extraEle={
                !clientUserAllPermissions ||
                clientUserAllPermissions.is_allow_to_view_contract_detail ===
                  1 ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={routingConstants.contractDetail(
                        row.route_contract_id,
                      )}
                      className="line-clamp-1 text-[12px] font-medium leading-[normal] text-txtColor"
                    >
                      {row.contract_ref_no}
                    </Link>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <span className="line-clamp-1 cursor-default text-[12px] font-medium leading-[normal] text-txtColor">
                      {row.contract_ref_no}
                    </span>
                  </div>
                )
              }
            />
          </div>
        </div>
      ),
      width: "18%",
    },
    {
      name: "Contact",
      sortable: true,
      sortField: "candidate_email",
      selector: (row) => (
        <div className="flex flex-col">
          <BlurredText type="phone">
            {getColumnNormalHtml(row.mobile_phone_number, "truncate")}
          </BlurredText>
          <BlurredText type="email">
            {getColumnNormalHtml(row.candidate_email, "truncate")}
          </BlurredText>
        </div>
      ),
      width: "15%",
    },
    {
      name: "Miles",
      selector: (row) => <Miles miles={row.miles} />,
      width: "8%",
    },
    {
      id: "flex-grow",
      name: "Role",
      sortable: true,
      sortField: "role_name",
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(
            row.role_name,
            "line-clamp-2 whitespace-break-spaces",
          )}
          <span className="text-[12px] font-medium leading-[normal] text-txtColor">
            {row.license_number}
          </span>
        </div>
      ),
      width: "8%",
    },
    {
      name: "Shift Date",
      sortable: true,
      sortField: "shift_date",
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(
            moment(row.shift_date, "DD/MM/YYYY").format("DD/MM/YYYY"),
          )}
          <span
            className={`h-[23px] w-[75px] rounded-[4px] text-center text-[14px] font-medium leading-[23px] ${getComplianceClass(
              row.profile_percent,
            )}`}
          >
            {row.profile_percent == "-" ? "0%" : row.profile_percent + "%"}
          </span>
        </div>
      ),
      width: "9%",
    },
    {
      name: "Shift",
      sortable: true,
      sortField: "start_time",
      selector: (row) => (
        <div className="flex flex-col items-end gap-1">
          {getColumnNormalHtml(
            moment(row.sign_off_start_time, "HH:mm:ss").format("HH:mm"),
          )}
          <span className="text-[14px] font-medium leading-[0px] text-black">
            -
          </span>
          {getColumnNormalHtml(
            moment(row.sign_off_end_time, "HH:mm:ss").format("HH:mm"),
          )}
        </div>
      ),
      width: "70px",
    },
    {
      name: "Break/Admin",
      sortable: true,
      sortField: "unpaid_break_time",
      selector: (row) => (
        <div className="flex flex-col items-end gap-1">
          {getColumnNormalHtml(
            moment(row.sign_off_unpaid_break_time, "HH:mm:ss").format("HH:mm"),
          )}
          <span className="text-[14px] font-medium leading-[0px] text-black">
            -
          </span>
          {getColumnNormalHtml(
            moment(row.sign_off_admin_time, "HH:mm:ss").format("HH:mm"),
          )}
        </div>
      ),
      width: "110px",
    },
    {
      name: "Visit",
      sortable: true,
      sortField: "no_of_visit",
      selector: (row) => (
        <div className="flex flex-col items-end gap-1">
          {getColumnNormalHtml(
            row.sign_off_no_of_visit ? row.sign_off_no_of_visit : 0,
          )}
        </div>
      ),
      width: "60px",
    },
    {
      name: "Unit",
      sortable: false,
      selector: (row) => (
        <div className="flex flex-col gap-1" data-tag="allowRowEvents">
          <span className="text-[14px] font-medium leading-[normal] text-black">
            {row.contract_duration !== "-"
              ? getColumnNormalHtml(
                  Number(`${row.candidate_contract_duration}`).toFixed(2),
                )
              : "0.00"}
          </span>
        </div>
      ),
      width: "60px",
    },
    {
      name: "Total",
      sortable: false,
      selector: (row) => (
        <div className="grid gap-1">
          <ClientPayPopover
            pay_arr={row?.sign_off_client_pay_arr}
            shift_rate={
              Number(row.sign_off_shift_rate) +
              Number(row.upcoming_reward_amount_for_cover_request || 0) -
              Number(row.upcoming_penalty_amount_for_cover_request || 0)
            }
            is_substitute_contract={
              row?.has_upcoming_penalty_for_cover_request ||
              row?.has_upcoming_reward_for_cover_request
            }
            cover_request_awarded_penalty={
              row?.upcoming_penalty_amount_for_cover_request
            }
            cover_request_awarded_reward={
              row.upcoming_reward_amount_for_cover_request
            }
            content={(content) => (
              <div className="flex flex-col gap-1" data-tag="allowRowEvents">
                {getColumnNormalHtml(content, "cursor-pointer")}
              </div>
            )}
          />
        </div>
      ),
      width: "120px",
    },
    expandButtonColumn(),
  ];
  const Expandedrow = ({ data }) => {
    return (
      <div className="flex h-[60px] items-center justify-end border-b border-b-lightblue bg-[#FCFCFC] pr-[156px]">
        <div className="pr-6 text-[12px] font-medium uppercase leading-[noraml] text-txtColor">
          Contracted hours
        </div>
        <div className="flex w-[70px] pl-[16px]">
          <div className="inline-flex flex-col items-end">
            <span className="text-[14px] font-medium leading-[normal] text-black">
              {data.start_time}
            </span>
            <span className="text-[14px] font-medium leading-[0px] text-black">
              -
            </span>
            <span className="text-[14px] font-medium leading-[normal] text-black">
              {data.end_time}
            </span>
          </div>
        </div>
        <div className="flex w-[113px] pl-[16px]">
          <div className="inline-flex flex-col items-end">
            <span className="text-[14px] font-medium leading-[normal] text-black">
              {data.break_time}
            </span>
            <span className="text-[14px] font-medium leading-[0px] text-black">
              -
            </span>
            <span className="text-[14px] font-medium leading-[normal] text-black">
              {data.admin_time}
            </span>
          </div>
        </div>
        <div className="flex w-[60px] flex-col pl-[16px]">
          <span className="text-[14px] font-medium leading-[normal] text-black">
            {data.no_of_visit || 0}
          </span>
        </div>
        <div className="flex w-[60px] flex-col pl-[16px]">
          <span className="text-[14px] font-medium leading-[normal] text-black">
            {data.contract_duration !== "-"
              ? getColumnNormalHtml(
                  Number(`${data.contract_duration}`).toFixed(2),
                )
              : "0.00"}
          </span>
        </div>
        <div className="flex w-[110px] pl-[16px]">
          <ClientPayPopover
            pay_arr={data?.sign_off_client_pay_arr}
            shift_rate={
              Number(data.sign_off_shift_rate) +
              Number(data.upcoming_reward_amount_for_cover_request || 0) -
              Number(data.upcoming_penalty_amount_for_cover_request || 0)
            }
            is_substitute_contract={
              data?.has_upcoming_penalty_for_cover_request ||
              data?.has_upcoming_reward_for_cover_request
            }
            cover_request_awarded_penalty={
              data?.upcoming_penalty_amount_for_cover_request
            }
            cover_request_awarded_reward={
              data.upcoming_reward_amount_for_cover_request
            }
            content={(content) => (
              <div className="flex flex-col gap-1" data-tag="allowRowEvents">
                {getColumnNormalHtml(content, "cursor-pointer")}
              </div>
            )}
          />
        </div>
      </div>
    );
  };

  // Handles the scroll event on a table and fetches booked candidates if scrolled to the bottom.
  const dataTableRef = useRef();

  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchSubmittedTimesheets({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  useEffect(() => {
    if (diffList.length > 0) {
      setTimeout(() => {
        setDiffList([]);
      }, 10000);
    }
  }, [diffList]);

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = sorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateSorting({ sortByColumn: newSortField, sortByOrder: newSortOrder }),
    );
    dispatch(deSelectAllCandidate());
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(setSubmittedTimesheetsLoading(true));
    dispatch(fetchSubmittedTimesheets());
  };

  return (
    <div className="SubmittedList_Wrapper mb-[58px]">
      <div className="flex h-full flex-col rounded-[10px] bg-white p-4 shadow-[0px_x1px_6px_0px_#E7EFFB]">
        <div className="mb-[20px] flex flex-col flex-wrap gap-3 border-b border-b-[#E8E8E8] pb-[11px] md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2 md:gap-[21px]">
            <h4 className="main-heading tour-submitted-heading font-medium leading-[normal] text-black">
              Submitted Timesheet
            </h4>
            {/* <Actions diffList={diffList} setDiffList={setDiffList} />
            <SelectedCountText
              selectedCount={selectedCandidate.length}
              totalCount={totalCount}
              isSelectedAll={isSelectedAll}
              clearSelected={() => {
                dispatch(updateToggleSelected());
              }}
              handleSelectAll={(val) => {
                dispatch(toggleSelectedAll(val));
              }}
              updateSelected={(val) => {
                dispatch(updateSelectedCandidate(val));
              }}
              currentDataLength={data.length}
            /> */}
          </div>
          <div className="flex gap-[20px]">
            <SearchFilter />
            <FilterModal />
          </div>
        </div>
        <div
          className="DataTable_Wrapper hide_extended_arrow"
          ref={dataTableRef}
        >
          <DataTable
            columns={columns}
            data={data}
            onSort={handleSort}
            // persistTableHead={true}
            fixedHeader
            fixedHeaderScrollHeight="600px"
            progressPending={isLoading}
            progressComponent={<TableLoader rows={5} />}
            selectableRows
            onSelectedRowsChange={(state) => {
              let selectedIDS = state.selectedRows.map((item) => {
                return item.candidate_id;
              });
              let selectedSignOff = state.selectedRows.map((item) => {
                return item.sign_off_id;
              });
              dispatch(updateSelectedCandidate(selectedIDS));
              dispatch(updateSelectedSignOffIds(selectedSignOff));
            }}
            noDataComponent={<TableNoContent rows={5} />}
            clearSelectedRows={toggleSelected}
            expandableRows
            expandOnRowClicked
            expandableRowsComponent={Expandedrow}
            className="_DataTable"
          />
        </div>
        {!isLoading && isPaginationLoading ? (
          <DataTablePaginationLoader />
        ) : null}
      </div>
    </div>
  );
};

export default SubmittedList;
